'use client';

import * as React from 'react';
import { Loader2, Maximize, Minimize, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { formatTime } from './playerTime';

const SPEEDS = [1, 1.5, 2, 0.5] as const;

/**
 * Custom overlay controls in our own brand theme, matching
 * AudioWaveformPlayer's premium feel — the browser's native `<video
 * controls>` bar looks like a different, unstyled product bolted onto this
 * page (font, icon set, colors — none of it ours).
 */
export function VideoPlayer({
  src,
  onError,
  ariaLabel,
}: {
  src: string;
  onError: () => void;
  ariaLabel: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [buffering, setBuffering] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [muted, setMuted] = React.useState(false);
  const [speedIndex, setSpeedIndex] = React.useState(0);
  const [fullscreen, setFullscreen] = React.useState(false);

  const progress = duration > 0 ? currentTime / duration : 0;

  React.useEffect(() => {
    const onFsChange = () => setFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) el.pause();
    else void el.play();
  };

  const seekTo = (fraction: number) => {
    const el = videoRef.current;
    if (!el || !duration) return;
    el.currentTime = Math.min(duration, Math.max(0, fraction * duration));
  };

  const onSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo((e.clientX - rect.left) / rect.width);
  };

  const cycleSpeed = () => {
    const next = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(next);
    if (videoRef.current) videoRef.current.playbackRate = SPEEDS[next];
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void containerRef.current?.requestFullscreen();
  };

  return (
    <div
      ref={containerRef}
      className="group relative aspect-video w-full overflow-hidden rounded-[var(--radius-md)] bg-black"
    >
      <video
        ref={videoRef}
        src={src}
        preload="metadata"
        onError={onError}
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        aria-label={ariaLabel}
        className="h-full w-full cursor-pointer object-contain"
      />

      {buffering && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/90" aria-hidden="true" />
        </div>
      )}

      {/* Center play button — always shown while paused, only on hover while playing. */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Play'}
        className={`absolute inset-0 m-auto grid h-14 w-14 place-items-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-opacity ${
          playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
        }`}
      >
        {playing ? (
          <Pause className="h-6 w-6" fill="currentColor" aria-hidden="true" />
        ) : (
          <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" aria-hidden="true" />
        )}
      </button>

      {/* Bottom controls bar — fades in on hover/pause, like every premium video player. */}
      <div
        className={`absolute inset-x-0 bottom-0 space-y-2 bg-gradient-to-t from-black/85 to-transparent px-3 pb-2.5 pt-8 transition-opacity ${
          playing
            ? 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
            : 'opacity-100'
        }`}
      >
        <div
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(currentTime)}
          onClick={onSeekClick}
          onKeyDown={(e) => {
            const el = videoRef.current;
            if (!el || !duration) return;
            if (e.key === 'ArrowRight') el.currentTime = Math.min(duration, el.currentTime + 5);
            else if (e.key === 'ArrowLeft') el.currentTime = Math.max(0, el.currentTime - 5);
            else return;
            e.preventDefault();
          }}
          className="h-3 w-full cursor-pointer outline-none"
        >
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/25">
            <div
              style={{ width: `${progress * 100}%` }}
              className="h-full rounded-full bg-[var(--primary)] transition-[width]"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? 'Pause' : 'Play'}
            className="text-white/90 hover:text-white"
          >
            {playing ? (
              <Pause className="h-4 w-4" fill="currentColor" aria-hidden="true" />
            ) : (
              <Play className="h-4 w-4" fill="currentColor" aria-hidden="true" />
            )}
          </button>

          <span className="text-xs tabular-nums text-white/80">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <span className="flex-1" />

          <button
            type="button"
            onClick={cycleSpeed}
            aria-label="Playback speed"
            className="rounded-full border border-white/30 px-2 py-0.5 text-xs font-medium text-white/90 hover:bg-white/10"
          >
            {SPEEDS[speedIndex]}x
          </button>

          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            className="text-white/90 hover:text-white"
          >
            {muted ? (
              <VolumeX className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Volume2 className="h-4 w-4" aria-hidden="true" />
            )}
          </button>

          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            className="text-white/90 hover:text-white"
          >
            {fullscreen ? (
              <Minimize className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Maximize className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
